import { onBeforeMount } from "vue"

/**
 * initLinkTag injects a stylesheet link tag into the head
 * @param url link tag href attribute value
 * @returns Promise<void>
 */
export const initLinkTag = (url: string) => {
  return new Promise<void>((resolve, reject) => {
    const tag = document.createElement("link")
    tag.rel = "stylesheet"
    tag.href = url
    tag.onload = () => {
      return resolve()
    }

    // NOTE(spk): tag onerror events are a generic HTML events and contain no useful details
    tag.onerror = () => {
      reject(new Error(`Failed to inject link tag with src ${url}`))
    }

    document.head.insertBefore(
      tag,
      document.head.childNodes[document.head.childNodes.length - 1].nextSibling
    )
  })
}

/**
 * initScriptTag injects a javascript script tag into the head
 * @param url script tag src attribute value
 * @returns Promise<void>
 */
export const initScriptTag = (url: string) => {
  return new Promise<void>((resolve, reject) => {
    const tag = document.createElement("script")
    tag.type = "text/javascript"
    tag.src = url
    tag.async = true

    tag.onload = () => {
      return resolve()
    }

    // NOTE(spk): tag onerror events are a generic HTML events and contain no useful details
    tag.onerror = () => {
      reject(new Error(`Failed to inject script tag with src ${url}`))
    }

    document.head.insertBefore(
      tag,
      document.head.childNodes[document.head.childNodes.length - 1].nextSibling
    )
  })
}

interface PageMeta {
  title: string
  meta: {
    name: string
    content: string
  }[]
}

/**
 * usePageMeta sets the domcument title and adds or updates meta tags
 * @param config PageMeta
 */
export const usePageMeta = (config: PageMeta) => {
  onBeforeMount(() => {
    const head = document.querySelector("head")

    if (head === null) {
      return
    }

    if (config.title) {
      document.title = config.title
    }

    config.meta.forEach((meta) => {
      if (!meta.name || !meta.content) {
        return
      }

      const existing = document.querySelector(`meta[name="${meta.name}"]`)
      if (existing) {
        existing.setAttribute("name", meta.name)
        existing.setAttribute("content", meta.content)
        return
      }

      const tag = document.createElement("meta")
      tag.setAttribute("name", meta.name)
      tag.setAttribute("content", meta.content)
      head.appendChild(tag)
    })
  })
}
